"use client";

import { ArrowDown, Check, type LucideIcon, Phone, Voicemail } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

const SCOTT = { name: "Scott Tinari", src: "/images/website/customers/scott-tinari.jpg" };

/* ------------------------------------------------------------------ */
/* Shared mock chrome                                                 */
/* ------------------------------------------------------------------ */

function MiniCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · Omnichannel thread — every channel in one conversation         */
/* ------------------------------------------------------------------ */

function ChannelChip({ icon: Icon, label, sub }: { icon: LucideIcon; label: string; sub: string }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] shadow-sm">
        <Icon className="size-3 shrink-0 text-muted-foreground" />
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">· {sub}</span>
      </span>
    </div>
  );
}

function ChannelThreadMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <div className="flex">
        <div className="max-w-[82%] rounded-2xl rounded-bl-md border border-border bg-incoming-bubble px-3 py-1.5 text-[12px] leading-snug text-foreground">
          Can I bring a guest to Saturday's 9am?
        </div>
      </div>
      <ChannelChip icon={Phone} label="Call" sub="3m 20s" />
      <ChannelChip icon={Voicemail} label="Voicemail" sub="0:25" />
      <div className="flex justify-end">
        <div className="max-w-[86%] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-1.5 text-[12px] leading-snug text-white shadow-sm">
          Saved you both a spot. See you Saturday 💪
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Context — the contact profile beside the conversation          */
/* ------------------------------------------------------------------ */

function ContextRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className="text-[12px] font-medium text-foreground">{value}</span>
    </div>
  );
}

function ContextMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-border p-3">
        <Avatar name={SCOTT.name} src={SCOTT.src} size="sm" />
        <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">
          {SCOTT.name}
        </p>
        <Badge variant="outline" className="shrink-0 bg-background text-[10px]">
          <span className="mr-1.5 size-1.5 rounded-full bg-success" />
          Subscribed
        </Badge>
      </div>
      <div className="space-y-2 p-3">
        <ContextRow label="Classes" value="615" />
        <ContextRow label="Last visit" value="2 days ago" />
        <ContextRow label="Next class" value="Sat · 9:00 AM" />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Speed to response — team push, then auto-assignment            */
/* ------------------------------------------------------------------ */

function SpeedMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <MiniCard className="flex items-center gap-2.5 p-2.5">
        <Image
          src="/images/icon.png"
          width={32}
          height={32}
          alt=""
          className="size-7 shrink-0 rounded-[7px]"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12px] font-semibold text-foreground">New lead · team alerted</p>
          <p className="truncate text-[11px] text-muted-foreground">Maya: Any openings this weekend?</p>
        </div>
        <span className="shrink-0 text-[10px] text-muted-foreground">now</span>
      </MiniCard>

      <div className="flex justify-center">
        <ArrowDown className="size-3.5 text-muted-foreground" />
      </div>

      <MiniCard className="flex items-center gap-2 p-2.5">
        <Avatar name="Jordan Lee" size="xs" />
        <span className="min-w-0 flex-1 truncate text-[12px] text-foreground">
          <span className="font-semibold">Jordan</span> replied
        </span>
        <Badge variant="outline" className="shrink-0 bg-background text-[10px]">
          <Check className="mr-1 size-2.5 text-success" strokeWidth={3} />
          Assigned
        </Badge>
      </MiniCard>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: <ChannelThreadMock />,
    title: "Every channel, one thread",
    body: "Text, calls, voicemail, email, and form fills land in one conversation, never scattered across tools.",
  },
  {
    visual: <ContextMock />,
    title: "Context, not guesswork",
    body: "Every contact's history is one glance away, so any teammate can pick up the chat and reply like they know them.",
  },
  {
    visual: <SpeedMock />,
    title: "Speed to response, built in",
    body: "New leads alert your whole team. Whoever replies first owns the thread and can hand it off with a note anytime.",
  },
];

export function UnifiedInboxHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              From first hello to loyal customer, every conversation at your business lives
              in Helios.
              <span className="text-muted-foreground">
                {" "}
                Reply with full context and route each lead to the right teammate in seconds.
              </span>
            </>
          }
        />
      </div>

      {/* Framed band: hatched side gutters, solid card body (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[188px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
